import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { CACHE_TIME } from '@/config/queryDefaults';
import type { WidgetConfig } from './useDashboardWidgets';

export interface DynamicWidgetFilters {
  timeRangeKey: string;
  resolution?: string;
  legalEntityId?: string | null;
  businessUnitId?: string | null;
}

const READ_MODEL_BASE_URLS: Record<string, string> = {
  SalesDaily: '/api/sales/read-models/sales-daily',
  SalesMonthly: '/api/sales/read-models/sales-monthly',
  InventorySnapshot: '/api/inventory/read-models/inventory-snapshot',
};

/**
 * Maps a readModel + groupBy pair to an alternative readModel when a
 * coarser-resolution table already exists (e.g. SalesMonthly for monthly
 * grouping of SalesDaily data). This avoids transferring and re-aggregating
 * fine-grained rows on the client.
 */
const READ_MODEL_REDIRECTS: Record<string, { readModel: string; groupBy: string }> = {
  'SalesDaily:month': { readModel: 'SalesMonthly', groupBy: 'month' },
};

function getReadModelUrl(readModel: string): string {
  return READ_MODEL_BASE_URLS[readModel] ?? `/api/shared/read-models/${toKebabCase(readModel)}`;
}

function toKebabCase(str: string): string {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

function computeComparisonPeriod(timeRangeKey: string): { comparisonStartDate: string; comparisonEndDate: string } {
  const { startDate: startStr, endDate: endStr } = computeDateRange(timeRangeKey);
  const start = new Date(startStr);
  const end = new Date(endStr);
  const durationMs = end.getTime() - start.getTime();

  const comparisonEnd = new Date(start.getTime() - 1);
  const comparisonStart = new Date(comparisonEnd.getTime() - durationMs);

  return {
    comparisonStartDate: comparisonStart.toISOString(),
    comparisonEndDate: comparisonEnd.toISOString(),
  };
}

function computeDateRange(timeRangeKey: string): { startDate: string; endDate: string } {
  const now = new Date();
  const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  let startDate: Date;

  switch (timeRangeKey) {
    case 'last7days':
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 7);
      break;
    case 'last30days':
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 30);
      break;
    case 'last90days':
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 90);
      break;
    case 'last12months':
      startDate = new Date(endDate);
      startDate.setMonth(startDate.getMonth() - 12);
      break;
    case 'ytd':
      startDate = new Date(endDate.getFullYear(), 0, 1);
      break;
    default:
      startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 30);
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
}

export function useDynamicWidget(widget: WidgetConfig, filters: DynamicWidgetFilters) {
  const readModel = widget.readModel;
  const enabled = !!readModel;

  // Resolve redirect: use a coarser-resolution read model when available
  const redirectKey = readModel && widget.groupBy ? `${readModel}:${widget.groupBy}` : '';
  const redirect = redirectKey ? READ_MODEL_REDIRECTS[redirectKey] : undefined;
  const effectiveReadModel = redirect?.readModel ?? readModel;
  const effectiveGroupBy = redirect?.groupBy ?? widget.groupBy;

  return useQuery({
    queryKey: ['dynamicWidget', widget.id, effectiveReadModel, widget.type, widget.measure, effectiveGroupBy, widget.dimension, filters],
    retry: 1,
    staleTime: CACHE_TIME.DEFAULT,
    queryFn: async () => {
      if (!effectiveReadModel) return null;

      const { startDate, endDate } = computeDateRange(filters.timeRangeKey);
      const baseUrl = getReadModelUrl(effectiveReadModel);
      const params: Record<string, string> = { startDate, endDate };

      if (filters.resolution) params.resolution = filters.resolution;
      if (filters.legalEntityId) params.legalEntityId = filters.legalEntityId;
      if (filters.businessUnitId) params.businessUnitId = filters.businessUnitId;

      if (widget.type === 'kpiCard') {
        // KPI: query the measure endpoint with comparison period
        const comparison = computeComparisonPeriod(filters.timeRangeKey);
        const { data } = await apiClient.get(`${baseUrl}/kpi/${widget.measure}`, {
          params: { ...params, ...comparison },
        });
        return data;
      }

      if (widget.type === 'lineChart') {
        // Line chart: group by dimension (e.g., date) — backend aggregates all measures
        const chartParams = {
          ...params,
          ...(widget.dimension && { groupBy: widget.dimension }),
        };
        const { data } = await apiClient.get(`${baseUrl}/query`, { params: chartParams });
        return data;
      }

      if (widget.type === 'barChart') {
        // Bar chart: group by dimension — backend aggregates all measures
        const chartParams = {
          ...params,
          ...(effectiveGroupBy && { groupBy: effectiveGroupBy }),
        };
        const { data } = await apiClient.get(`${baseUrl}/query`, { params: chartParams });
        return data;
      }

      if (widget.type === 'table') {
        // Table: query with groupBy, sort, limit
        const tableParams = {
          ...params,
          ...(effectiveGroupBy && { groupBy: effectiveGroupBy }),
          ...(widget.sortBy && { sortBy: widget.sortBy }),
          ...(widget.sortDirection && { sortDirection: widget.sortDirection }),
          ...(widget.limit && { limit: String(widget.limit) }),
        };
        const { data } = await apiClient.get(`${baseUrl}/query`, { params: tableParams });
        return data;
      }

      return null;
    },
    enabled,
  });
}
